'use client';

import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';

import { cn } from '../../lib/cn';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg';

type SpinnerProps = {
  /** 스피너 크기. 기본값은 인라인 사용에 맞춘 "xs". */
  size?: SpinnerSize;
  /** text-primary, shrink-0 등 추가 modifier 주입용. */
  className?: string;
  /** 스크린리더용 라벨. 미지정 시 공통 "불러오는 중" 문구. */
  label?: string;
};

/**
 * DaisyUI `loading-spinner` 를 감싼 공통 인라인 로딩 인디케이터.
 * `role="status"` 와 `aria-label` 을 기본 내장해 접근성을 일괄 보장한다.
 * 버튼/폼/모달 등 인라인·국소 로딩에 사용한다. 전체 페이지 로딩은 `SpinnerScreen` 을 쓴다.
 */
export function Spinner({ size = 'xs', className, label }: SpinnerProps) {
  const t = useTranslations('common');
  return (
    <span
      className={cn('loading loading-spinner', `loading-${size}`, className)}
      role="status"
      aria-label={label ?? t('loading')}
    />
  );
}

// 전체 페이지 로딩은 브랜드 로티 로더로 표시한다. lottie-web 의존이 무거워 정적 import 하면
// 가벼운 인라인 Spinner 사용처까지 번들에 끌려오고 jsdom 테스트에서 import 시 캔버스 접근으로 깨진다.
// 따라서 lazy-load 하고, 폴백은 null 로 둔다 — 스피너→로티 이중 로딩 UI 없이 로티만 한 번 표시된다.
const AgentTeamsLottieLoader = dynamic(
  () => import('./AgentTeamsLottieLoader').then((mod) => mod.AgentTeamsLottieLoader),
  { ssr: false, loading: () => null },
);

type SpinnerScreenProps = {
  /** 센터링 래퍼에 추가할 클래스. */
  className?: string;
  /** 스크린리더용 라벨. 미지정 시 공통 "불러오는 중" 문구. */
  label?: string;
};

/**
 * 전체 페이지/영역 중앙 정렬 로딩 화면. 반복되던 센터링 래퍼 + 로더 조합을 흡수하며,
 * 로더는 브랜드 로티(`AgentTeamsLottieLoader`)를 lazy-load 로 렌더한다(폴백 null).
 * DaisyUI 폴백 스피너가 없어 로딩 UI가 한 번만(로티) 표시된다.
 */
export function SpinnerScreen({ className, label }: SpinnerScreenProps) {
  const t = useTranslations('common');
  return (
    <div className={cn('flex items-center justify-center py-24', className)}>
      <AgentTeamsLottieLoader label={label ?? t('loading')} />
    </div>
  );
}
